import { useI18next, useTranslation } from 'gatsby-plugin-react-i18next';
import React, { ReactNode, useContext, useEffect, useState } from 'react';
import { LANGUAGES_IMPERIAL_UNITS } from '../../../../constants/commonConstants';
import { ProductSearchResultsContext } from '../../../../context/ProductSearchResultsContext';
import MultiRangeSlider from '../../../Sliders/MultiRangeSlider/MultiRangeSlider';
import {
    FiltersContent,
    FiltersContentBox,
    FiltersContentLabel,
    FiltersContentWrapper,
    FiltersWrapper,
    InputWrapper,
    RadioButton,
} from './FilterBoxStyle';

const FilterBox = ({ filterName, filters, currentActiveCategory }) => {
    const {
        typeFilter,
        setTypeFilter,
        modeFilter,
        setModeFilter,
        pressureRangeFilter,
        setPressureRangeFilter,
        heatRecoveryFilter,
        setHeatRecoveryFilter,
        soundAbsorptionFilter,
        setSoundAbsorptionFilter,
        deliveryControlFilter,
        setDeliveryControlFilter,
        coolingFilter,
        setCoolingFilter,
        otherFilter,
        setOtherFilter,
        motorPowerMinFilter,
        setMotorPowerMinFilter,
        motorPowerMaxFilter,
        setMotorPowerMaxFilter,
        fedMinFilter,
        setFedMinFilter,
        fedMaxFilter,
        setFedMaxFilter,
        motorPowerMaxValue,
        fedMaxValue,
    } = useContext(ProductSearchResultsContext);

    const { t } = useTranslation();
    const [motorPowerMinValueState, setMotorPowerMinValueState] = useState(0);
    const [motorPowerMaxValueState, setMotorPowerMaxValueState] = useState(motorPowerMaxValue);
    const [fedMinValueState, setFedMinValueState] = useState(0);
    const [fedMaxValueState, setFedMaxValueState] = useState(fedMaxValue);
    const [currentLabelOfCompressionMode, setCurrentLabelOfCompressionMode] = useState('motor_power_metric');
    const key = 0;
    const value = 1;
    const equipmentFiltersLabel = ['heatRecovery', 'soundAbsorption', 'deliveryControl', 'cooling', 'otherProperties'];
    const { language } = useI18next();

    useEffect(() => {
        if (LANGUAGES_IMPERIAL_UNITS.indexOf(language) !== -1) {
            setCurrentLabelOfCompressionMode('motor_power_imperial');
        } else {
            setCurrentLabelOfCompressionMode('motor_power_metric');
        }
    }, [language]);

    const setMinMaxValueForSlider = (value: number, label: string): void => {
        if (label === 'motor_power_metric' || label === 'motor_power_imperial') {
            setMotorPowerMinValueState(0);
            setMotorPowerMaxValueState(+value);
        } else {
            setFedMinValueState(0);
            setFedMaxValueState(+value);
        }
    };

    const saveFilterToContext = (label: string, value: any) => {
        switch (label) {
            case 'type':
                return setTypeFilter((prevState) => prevState !== value && value);
            case 'mode':
                return setModeFilter((prevState) => prevState !== value && value);
            case 'pressure_range':
                return setPressureRangeFilter((prevState) => prevState !== value && value);
            case 'heat_recovery':
                return setHeatRecoveryFilter((prevState) => prevState !== value && value);
            case 'sound_absorption':
                const soundAbsorptionIndex = soundAbsorptionFilter.indexOf(value);
                if (soundAbsorptionIndex === -1) {
                    return setSoundAbsorptionFilter((prevState) => [...prevState, value]);
                } else {
                    return setSoundAbsorptionFilter((prevState) => {
                        prevState.splice(soundAbsorptionIndex, 1);
                        return [...prevState];
                    });
                }
            case 'delivery_control':
                const deliveryControlIndex = deliveryControlFilter.indexOf(value);
                if (deliveryControlIndex === -1) {
                    return setDeliveryControlFilter((prevState) => [...prevState, value]);
                } else {
                    return setDeliveryControlFilter((prevState) => {
                        prevState.splice(deliveryControlIndex, 1);
                        return [...prevState];
                    });
                }
            case 'cooling':
                const coolingIndex = coolingFilter.indexOf(value);
                if (coolingIndex === -1) {
                    return setCoolingFilter((prevState) => [...prevState, value]);
                } else {
                    return setCoolingFilter((prevState) => {
                        prevState.splice(coolingIndex, 1);
                        return [...prevState];
                    });
                }
            case 'others':
                const otherIndex = otherFilter.indexOf(value);
                if (otherIndex === -1) {
                    return setOtherFilter((prevState) => [...prevState, value]);
                } else {
                    return setOtherFilter((prevState) => {
                        prevState.splice(otherIndex, 1);
                        return [...prevState];
                    });
                }
            default:
                return '';
        }
    };

    const checkIfIsChecked = (label: string, value: any): boolean => {
        switch (label) {
            case 'type':
                return typeFilter === value;
            case 'mode':
                return modeFilter === value;
            case 'pressure_range':
                return pressureRangeFilter === value;
            case 'heat_recovery':
                return heatRecoveryFilter === value;
            case 'sound_absorption':
                return soundAbsorptionFilter.indexOf(value) !== -1;
            case 'delivery_control':
                return deliveryControlFilter.indexOf(value) !== -1;
            case 'cooling':
                return coolingFilter.indexOf(value) !== -1;
            case 'others':
                return otherFilter.indexOf(value) !== -1;
            case 'compression_mode':
                return currentLabelOfCompressionMode === value;
            default:
                return false;
        }
    };

    const displayMultiRangeSliders = (label: string): ReactNode => {
        if (label === 'motor_power_metric' || label === 'motor_power_imperial') {
            return (
                <MultiRangeSlider
                    key={label}
                    min={+motorPowerMinValueState}
                    max={+motorPowerMaxValueState}
                    setMinValueOfFilter={setMotorPowerMinFilter}
                    setMaxValueOfFilter={setMotorPowerMaxFilter}
                    minValueOfFilter={motorPowerMinFilter}
                    maxValueOfFilter={motorPowerMaxFilter}
                />
            );
        } else {
            return (
                <MultiRangeSlider
                    key={label}
                    min={+fedMinValueState}
                    max={+fedMaxValueState}
                    setMinValueOfFilter={setFedMinFilter}
                    setMaxValueOfFilter={setFedMaxFilter}
                    minValueOfFilter={fedMinFilter}
                    maxValueOfFilter={fedMaxFilter}
                />
            );
        }
    };

    const checkFiltersInputType = (widget: string, label: string, item: any, index: number): ReactNode => {
        switch (widget) {
            case 'checkboxes':
                if (filterName === 'equipment') {
                    return (
                        <FiltersContent isEquipment={true} key={label}>
                            <FiltersContentLabel>{t(equipmentFiltersLabel[index])}</FiltersContentLabel>
                            <FiltersContentBox>
                                {Object.entries(item[key])?.map((item: any, index: number) => (
                                    <InputWrapper key={`${label}${index}`}>
                                        <input
                                            type="checkbox"
                                            id={item[key]}
                                            name={label}
                                            value={item[key]}
                                            onClick={(e: any) => saveFilterToContext(label, e.target.value)}
                                            defaultChecked={checkIfIsChecked(label, item[key])}
                                        />
                                        <label htmlFor={item[key]}>{t(`${item[key]}`)}</label>
                                    </InputWrapper>
                                ))}
                            </FiltersContentBox>
                        </FiltersContent>
                    );
                } else {
                    return (
                        <FiltersContent key={label}>
                            {Object.entries(item[key])?.map((item: any, index: number) => (
                                <InputWrapper key={`${label}${index}`}>
                                    <input
                                        type="checkbox"
                                        id={item[key]}
                                        name={label}
                                        value={item[key]}
                                        onClick={(e: any) => saveFilterToContext(label, e.target.value)}
                                        defaultChecked={checkIfIsChecked(label, item[key])}
                                    />
                                    <label htmlFor={`${item[key]}`}>{t(`${item[key]}`)}</label>
                                </InputWrapper>
                            ))}
                        </FiltersContent>
                    );
                }
            case 'radio':
                return (
                    <FiltersContent key={label}>
                        {Object.entries(item[key])?.map((item: any, index: number) => (
                            <RadioButton key={`${label}${index}`} htmlFor={`${item[key]}`}>
                                {t(`${item[key]}`)}
                                <input
                                    type="radio"
                                    id={item[key]}
                                    value={item[key]}
                                    name={label}
                                    onClick={(e: any) => saveFilterToContext(label, e.target.value)}
                                    defaultChecked={checkIfIsChecked(label, item[key])}
                                />
                                <span></span>
                            </RadioButton>
                        ))}
                    </FiltersContent>
                );
            case 'slider':
                if (LANGUAGES_IMPERIAL_UNITS.indexOf(language) === -1 && item?.metric) {
                    return (
                        <FiltersContentWrapper key={label}>
                            <FiltersContent key={label}>
                                {Object.entries(item.metric)?.map((item: any, index: number) => (
                                    <RadioButton key={`${label}${index}`} htmlFor={`${item[key]}`}>
                                        {t(`${item[key]}`)}
                                        <input
                                            type="radio"
                                            id={item[key]}
                                            name={label}
                                            value={item[value].max}
                                            onClick={(e: any) => {
                                                saveFilterToContext(label, item[key]);
                                                setMinMaxValueForSlider(+e.target.value, item[key]);
                                                setCurrentLabelOfCompressionMode(item[key]);
                                            }}
                                            defaultChecked={checkIfIsChecked(label, item[key])}
                                        />
                                        <span></span>
                                    </RadioButton>
                                ))}
                            </FiltersContent>
                            {displayMultiRangeSliders(currentLabelOfCompressionMode)}
                        </FiltersContentWrapper>
                    );
                } else if (item?.imperial) {
                    return (
                        <FiltersContentWrapper key={label}>
                            <FiltersContent key={label}>
                                {Object.entries(item.imperial)?.map((item: any, index: number) => (
                                    <RadioButton key={`${label}${index}`} htmlFor={`${item[key]}`}>
                                        {t(`${item[key]}`)}
                                        <input
                                            type="radio"
                                            id={item[key]}
                                            name={label}
                                            value={item[value].max}
                                            onClick={(e: any) => {
                                                saveFilterToContext(label, item[key]);
                                                setMinMaxValueForSlider(+e.target.value, item[key]);
                                                setCurrentLabelOfCompressionMode(item[key]);
                                            }}
                                            defaultChecked={checkIfIsChecked(label, item[key])}
                                        />
                                        <span></span>
                                    </RadioButton>
                                ))}
                            </FiltersContent>
                            {displayMultiRangeSliders(currentLabelOfCompressionMode)}
                        </FiltersContentWrapper>
                    );
                } else {
                    return '';
                }
            default:
                return '';
        }
    };

    return (
        <>
            {currentActiveCategory === filterName && (
                <FiltersWrapper>
                    {filters?.map((item: any, index: number) =>
                        checkFiltersInputType(item.widget, item.label, item, index)
                    )}
                </FiltersWrapper>
            )}
        </>
    );
};

export default FilterBox;
